import AjaxCache from 'PlugAndPlay/_global/js/ajaxCache';
import { geoContains } from 'd3-geo';
import { getCurrentRegion } from '../../_global/js/regions';
import Combobox from '../../combobox/js/global';
import SEQ_POLYGON from '../polygons/seq-polygon.json';
import QLD_POLYGON from '../polygons/qld-polygon.json';

export default function _autocomplete() {
    /**
     * Autocomplete wrapper selector
     * @const {string}
     */
    const AUTOCOMPLETE_SELECTOR = '[data-component="autocomplete"]';

    /**
     * Selector for autocomplete listbox status area
     * const {string}
     */
    const AUTOCOMPLETE_RESULT_STATS_SELECTOR =
        '[data-component="autocomplete-result-status"]';
    /**
     * Selector for autocomplete listbox status area count
     * @const {string}
     */
    const AUTOCOMPLETE_RESULT_COUNT_SELECTOR =
        '[data-component="autocomplete-result-count"]';

    // const AUTOCOMPLETE_RESULT_TEMPLATE = 'template.autocomplete-result';
    // const AUTOCOMPLETE_RESULT_QUERY_TEMPLATE = 'template.autocomplete-result-query';

    const AUTOCOMPLETE_RESULT_TEMPLATE =
        'template[data-template-id="autocomplete-result"]';
    const AUTOCOMPLETE_RESULT_QUERY_TEMPLATE =
        'template[data-template-id="autocomplete-result-query"]';

    const AZURE_GEOCODER =
        'https://www.energex.com.au/project-x/_design/integration-points/locationfinder/arcgis-reverse-geocoder';

    const AUTOCOMPLETE_FB_FORM_PARENT =
        '.funnelback-search__form,.header-search__form';
    const AUTOCOMPLETE_CLEAR_BUTTON = '.funnelback-search__clear';
    /**
     * Create a new Autocomplete.
     *
     * @param {HTMLElement} autocomplete - The wrapping HTML element of the autocomplete
     * @class
     */

    class Autocomplete extends Combobox {
        constructor(autocomplete) {
            super(autocomplete);

            this.autocomplete = autocomplete;
            this.resultStatus = autocomplete.querySelector(
                AUTOCOMPLETE_RESULT_STATS_SELECTOR
            );
            this.resultStatusCount = autocomplete.querySelector(
                AUTOCOMPLETE_RESULT_COUNT_SELECTOR
            );
            this.emphasis = autocomplete.dataset.emphasis;

            this.autoCompleteClearButton = autocomplete.querySelector(
                AUTOCOMPLETE_CLEAR_BUTTON
            );

            this.funnelbackSearchForm = autocomplete.closest(
                AUTOCOMPLETE_FB_FORM_PARENT
            );

            // Get the templates for the results list from the DOM
            this.resultItemTemplate = autocomplete.querySelector(
                AUTOCOMPLETE_RESULT_TEMPLATE
            ).content.firstElementChild;

            this.resultItemQueryTemplate = autocomplete.querySelector(
                AUTOCOMPLETE_RESULT_QUERY_TEMPLATE
            ).content.firstElementChild;

            // Build the remote address URL string
            if (
                autocomplete.dataset.autocompleteType === 'azure-place' ||
                autocomplete.dataset.autocompleteType === 'i-want-to'
            ) {
                this.remoteAddress = `${this.autocomplete.dataset.suggestSource}`;
            } else {
                this.remoteAddress = `${this.autocomplete.dataset.suggestSource}`;
                this.parameters = `?collection=${this.autocomplete.dataset.suggestCollection}&${this.autocomplete.dataset.suggestAdditionalParams}&fmt=json++`;
            }

            // Create an ajax request cache
            this.ajaxCache = new AjaxCache('autocomplete');

            if (this.autoCompleteClearButton !== null) {
                this.input.addEventListener('keyup', (e) => {
                    this.setClearButton(e);
                });
                this.autoCompleteClearButton.addEventListener(
                    'click',
                    () => {
                        this.clearTextInput();
                    },
                    { once: true }
                );
                if (this.input.value.length > 0) {
                    this.autoCompleteClearButton.classList.remove('hidden');
                } else {
                    this.autoCompleteClearButton.classList.add('hidden');
                }
            }
        }

        setClearButton(event) {
            const { currentTarget } = event;
            if (currentTarget.value.length > 0) {
                if (this.autoCompleteClearButton.classList.contains('hidden')) {
                    this.autoCompleteClearButton.classList.remove('hidden');
                    this.autoCompleteClearButton.addEventListener(
                        'click',
                        () => {
                            this.clearTextInput();
                        },
                        { once: true }
                    );
                }
            } else {
                this.autoCompleteClearButton.classList.add('hidden');
            }
        }

        clearTextInput() {
            this.input.value = '';
            this.autoCompleteClearButton.classList.add('hidden');
            this.updateResults();
        }

        /**
         * Query funnelback suggest.json endpoint and render results to the listbox
         */
        updateResults() {
            if (this.autocomplete.dataset.autocompleteType === 'i-want-to') {
                const button =
                    this.autocomplete.parentNode.querySelector(
                        '.i-want-to__button'
                    );
                button.setAttribute(
                    'href',
                    `${this.autocomplete.dataset.search}?query=${this.input.value}`
                );
            }

            if (this.input.value.length > 2) {
                let url = '';
                const query = this.input.value;
                if (
                    this.autocomplete.dataset.autocompleteType === 'azure-place'
                ) {
                    const input = this.input.value.replaceAll(' ', ',');
                    url = `${this.remoteAddress}?term=${input}`;
                } else if (
                    this.autocomplete.dataset.autocompleteType === 'i-want-to'
                ) {
                    const currentUrl = window.location.href;
                    let exclude = '';

                    if (currentUrl.includes('seq')) {
                        exclude = '&location=qld-only';
                    } else if (currentUrl.includes('qld')) {
                        exclude = '&location=seq-only';
                    }
                    url = `${this.remoteAddress}&term=${this.input.value}${exclude}`;
                } else {
                    url = `${this.remoteAddress}${this.parameters}&partial_query=${this.input.value}`;
                }
                this.ajaxCache
                    .sendRequest({
                        url,
                        transform: (response) => {
                            return response.data;
                        },
                    })
                    .then((response) => {
                        // Remove all child nodes
                        this.clearResults();
                        // Create new child nodes
                        if (
                            this.autocomplete.dataset.autocompleteType ===
                            'azure-place'
                        ) {
                            response.suggestions.forEach((result, index) => {
                                this.listbox.appendChild(
                                    this.createAzurePlaceResultItemFromTemplate(
                                        result,
                                        index
                                    )
                                );
                            });
                        } else if (
                            this.autocomplete.dataset.autocompleteType ===
                            'i-want-to'
                        ) {
                            response.suggestions.forEach((result, index) => {
                                this.listbox.appendChild(
                                    this.createIWantToResultItemFromTemplate(
                                        result,
                                        index,
                                        query
                                    )
                                );
                            });
                        } else {
                            response.forEach((result, index) => {
                                this.listbox.appendChild(
                                    this.createResultItemFromTemplate(
                                        result,
                                        index,
                                        query
                                    )
                                );
                            });
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    })
                    .finally(() => {
                        // Post update call the super to re-setup the listbox
                        super.updateResults();
                    });
            } else {
                this.clearResults();
            }
        }

        /**
         * Creates a new results item from the template loaded onto the page.
         *
         * @param {Object} result - response from Funnelback request
         * @param {int} index - index of result
         * @returns {Element} - new <li> element for listbox
         */
        createResultItemFromTemplate(result, index, query) {
            const resultItem = this.resultItemTemplate.cloneNode(true);
            // Append the index to the id
            resultItem.setAttribute(
                'id',
                `${resultItem.getAttribute('id')}${index}`
            );

            // Add the result text
            if (this.emphasis && this.emphasis !== 'none') {
                const queryItem = this.resultItemQueryTemplate.cloneNode(true);
                queryItem.textContent = query;

                // Split the result display based on the query
                const querySplit = result.disp.split(query);
                querySplit.forEach((partWord, partIndex) => {
                    // If the part word isnt blank, print it
                    if (partWord) {
                        resultItem.appendChild(
                            document.createTextNode(partWord)
                        );
                    }
                    /*
                        If the partWord isnt the last item, print the 'query' span. A word like 'dedicated' will when split on 'd'
                        break into ["", "e", "icate", ""], the last partWord it doesnt need to be printed for both the end results.
                    */
                    if (partIndex !== querySplit.length - 1) {
                        resultItem.appendChild(queryItem.cloneNode(true));
                    }
                });
            } else {
                resultItem.textContent = result.disp;
            }

            return resultItem;
        }

        createIWantToResultItemFromTemplate(result, index) {
            const resultItem = this.resultItemTemplate.cloneNode(true);
            // Append the index to the id
            resultItem.setAttribute(
                'id',
                `${resultItem.getAttribute('id')}${index}`
            );

            resultItem.setAttribute('data-url', `${result.url}`);

            // Add the result text
            resultItem.textContent = result.title;

            return resultItem;
        }

        createAzurePlaceResultItemFromTemplate(result, index) {
            const resultItem = this.resultItemTemplate.cloneNode(true);
            // Append the index to the id
            resultItem.setAttribute(
                'id',
                `${resultItem.getAttribute('id')}${index}`
            );

            resultItem.setAttribute('data-place-id', `${result.magicKey}`);

            // Add the result text
            resultItem.textContent = result.text;

            return resultItem;
        }

        /**
         * Example of how to do on a system which does not support html templates
         *
         * @param {Object} result - response from Funnelback request
         * @param {int} index - index of result
         * @returns {Element} - new <li> element for listbox
         */
        createResultItem(result, index) {
            const resultItem = document.createElement('li');
            resultItem.textContent = result.disp;
            resultItem.setAttribute('id', `result-item-${index}`);
            resultItem.setAttribute('role', 'option');
            resultItem.setAttribute('aria-selected', 'false');
            resultItem.classList.add('autocomplete-listbox__item');
            return resultItem;
        }

        getPlaceDetails(id) {
            const url = `${AZURE_GEOCODER}?magicKey=${id}`;
            this.ajaxCache
                .sendRequest({
                    url,
                })
                .then((response) => {
                    const point = [
                        response.data.locations[0].feature.geometry.x,
                        response.data.locations[0].feature.geometry.y,
                    ];
                    const result =
                        this.autocomplete.parentElement.parentElement.querySelector(
                            '.autocomplete-location__results'
                        );

                    const currentRegion = getCurrentRegion();
                    const changeBtn =
                        this.autocomplete.parentElement.parentElement.querySelector(
                            '.location-modal__change'
                        );
                    const closeBtn =
                        this.autocomplete.parentElement.parentElement.querySelector(
                            '.location-modal__close'
                        );
                    const headerChange =
                        this.autocomplete.parentElement.parentElement.querySelector(
                            '.autocomplete-location__change'
                        );
                    const headerClose =
                        this.autocomplete.parentElement.parentElement.querySelector(
                            '.autocomplete-location__close'
                        );
                    let textResult = '<p>';

                    if (geoContains(SEQ_POLYGON, point)) {
                        textResult += 'You are in South East Queensland. ';
                        if (currentRegion === 'seq') {
                            textResult +=
                                'You are already seeing the information for your region.';
                            if (changeBtn) {
                                changeBtn.classList.add('hidden');
                            }
                            if (closeBtn) {
                                closeBtn.classList.remove('hidden');
                            }
                            if (headerChange) {
                                headerChange.classList.add('hidden');
                            }
                            if (headerClose) {
                                headerClose.classList.remove('hidden');
                            }
                        } else {
                            textResult +=
                                'Please click the button below to see the information for your region.';
                            if (changeBtn) {
                                changeBtn.setAttribute(
                                    'data-region-change',
                                    'seq'
                                );
                                changeBtn.classList.remove('hidden');
                            }
                            if (closeBtn) {
                                closeBtn.classList.add('hidden');
                            }
                            if (headerChange) {
                                headerChange.classList.remove('hidden');
                            }
                            if (headerClose) {
                                headerClose.classList.add('hidden');
                            }
                        }
                    } else if (geoContains(QLD_POLYGON, point)) {
                        textResult += 'You are in Greater Queensland. ';
                        if (currentRegion === 'qld') {
                            textResult +=
                                'You are already seeing the information for your region.';
                            if (changeBtn) {
                                changeBtn.classList.add('hidden');
                            }
                            if (closeBtn) {
                                closeBtn.classList.remove('hidden');
                            }
                            if (headerChange) {
                                headerChange.classList.add('hidden');
                            }
                            if (headerClose) {
                                headerClose.classList.remove('hidden');
                            }
                        } else {
                            textResult +=
                                'Please click the button below to see the information for your region.';
                            if (changeBtn) {
                                changeBtn.setAttribute(
                                    'data-region-change',
                                    'qld'
                                );
                                changeBtn.classList.remove('hidden');
                            }
                            if (closeBtn) {
                                closeBtn.classList.add('hidden');
                            }
                            if (headerChange) {
                                headerChange.classList.remove('hidden');
                            }
                            if (headerClose) {
                                headerClose.classList.add('hidden');
                            }
                        }
                    } else {
                        textResult +=
                            'The location you provided is not in any of the regions.';
                    }

                    textResult += '</p>';
                    result.innerHTML = textResult;
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        selectItem(item) {
            if (item) {
                this.input.value = item.innerText;
                if (item.dataset.placeId) {
                    this.getPlaceDetails(item.dataset.placeId);
                }
                if (item.dataset.url) {
                    const button =
                        this.autocomplete.parentNode.querySelector(
                            '.i-want-to__button'
                        );
                    button.setAttribute('href', item.dataset.url);
                    window.location.href = item.dataset.url;
                }
                this.input.dispatchEvent(new window.Event('change'));
                this.hideAutocompleteList();
                if (this.funnelbackSearchForm !== null) {
                    this.funnelbackSearchForm.submit();
                }
            }
        }
    }

    // Finall all autocomplete and created handler objects
    const autocompletes = document.querySelectorAll(AUTOCOMPLETE_SELECTOR);
    autocompletes.forEach((autocomplete) => {
        // Create a new item to modify
        const DOMItem = autocomplete;

        // Attach the autocomplete instance to our DOM Item
        if (!DOMItem.autocomplete) {
            DOMItem.autocomplete = new Autocomplete(autocomplete);
        }
    });
}

_autocomplete();
